import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CardContent, CircularProgress, Typography } from '@mui/material';
import { AlertTriangle, Package, Wifi } from 'lucide-react';
import { VendingMachineIcon } from '../../Components/Icons';
import { Header } from '../../Components/Header/Header';
import vendingApiClient from '../../Services/VendingApi';
import { formatUTC } from '../../Services/FormatUtc';
import { PageContainer } from '../../Styles/AppStyles';
import { COLORS } from '../../Styles/Colors';
import { FreshnessBadge } from '../../Components/Vending/FreshnessBadge';
import { VendingKpiCard } from '../../Components/Vending/VendingStyles';
import {
  AttentionItem,
  VendingDashboardData,
  VendingDashboardResponse,
  VendingMachineListResponse,
  VendingMachineSummary,
  VendingRestockQueueItem,
  VendingRestockQueueResponse,
} from '../../Types/vending';
import { AttentionRequired } from './AttentionRequired';

interface KpiCardProps {
  label: string;
  value: number;
  sublabel?: string;
  icon: React.ReactNode;
  accent?: string;
}

const KpiCard = ({ label, value, sublabel, icon, accent = COLORS.teal }: KpiCardProps) => (
  <VendingKpiCard>
    <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography
          variant="body2"
          sx={{
            color: COLORS.lightGray,
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontSize: '0.7rem',
          }}
        >
          {label}
        </Typography>
        <Box sx={{ color: accent, opacity: 0.9 }}>{icon}</Box>
      </Box>
      <Typography variant="h3" fontWeight="bold" sx={{ color: accent, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {sublabel && (
        <Typography variant="body2" sx={{ mt: 0.75, color: COLORS.lightGray }}>
          {sublabel}
        </Typography>
      )}
    </CardContent>
  </VendingKpiCard>
);

const minutesSince = (dateString: string | null): number | null => {
  if (!dateString) return null;
  const ms = Date.now() - new Date(dateString).getTime();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.floor(ms / 60000));
};

const formatLastCommunication = (dateString: string | null): string => {
  if (!dateString) return 'Unknown';
  return new Date(dateString).toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const locationLabel = (machine: VendingMachineSummary | VendingRestockQueueItem): string => {
  if ('location_name' in machine && machine.location_name) return machine.location_name;
  const parts = [machine.city, machine.state].filter(Boolean);
  if (parts.length > 0) return parts.join(', ');
  if ('customer_label' in machine && machine.customer_label) return machine.customer_label;
  if (machine.location_map_status === 'unmapped') return 'Unassigned';
  return 'Unknown location';
};

const customerLabel = (machine: VendingRestockQueueItem): string => {
  if (machine.customer_name) return machine.customer_name;
  if (machine.customer_label) return machine.customer_label;
  return locationLabel(machine);
};

const buildAttentionItems = (
  machines: VendingMachineSummary[],
  restockItems: VendingRestockQueueItem[],
): AttentionItem[] => {
  const items: AttentionItem[] = [];

  machines
    .filter((m) =>
      m.connection_status === 'offline'
      || m.status === 'critical'
      || m.status === 'action_required',
    )
    .sort((a, b) => {
      const aTime = a.last_communication ? new Date(a.last_communication).getTime() : 0;
      const bTime = b.last_communication ? new Date(b.last_communication).getTime() : 0;
      return aTime - bTime;
    })
    .forEach((machine) => {
      const mins = minutesSince(machine.last_communication);
      const loc = locationLabel(machine);
      const name = machine.machine_name || `Machine ${machine.provider_machine_id}`;

      items.push({
        id: `critical-${machine.id}`,
        severity: 'critical',
        title: `${loc} — ${name}`,
        lines: [
          mins != null
            ? `Machine has not communicated for ${mins} minute${mins === 1 ? '' : 's'}`
            : 'Machine communication status is unknown',
          `Last communication: ${formatLastCommunication(machine.last_communication)}`,
        ],
        actionLabel: 'View Machine →',
        actionPath: `/vending/machines/${machine.id}`,
      });
    });

  restockItems.forEach((item) => {
    const customer = customerLabel(item);
    const loc = item.location_name || locationLabel(item);
    const name = item.machine_name || `Machine ${item.machine_id}`;
    const belowMin = item.low_slots + item.empty_slots;

    items.push({
      id: `restock-${item.machine_id}`,
      severity: 'restock',
      title: `${customer} — ${name}`,
      lines: [
        `${belowMin} product${belowMin === 1 ? '' : 's'} below minimum • ${item.empty_slots} product${item.empty_slots === 1 ? '' : 's'} empty`,
        `${loc} • Estimated refill: ${item.total_units ?? item.estimated_refill_units} unit${(item.total_units ?? item.estimated_refill_units) === 1 ? '' : 's'}`,
      ],
      actionLabel: 'View Restock →',
      actionPath: `/vending/restock?machineId=${item.machine_id}`,
    });
  });

  return items;
};

export const VendingDashboardPage = () => {
  const [dashboard, setDashboard] = useState<VendingDashboardData | null>(null);
  const [machines, setMachines] = useState<VendingMachineSummary[]>([]);
  const [restockQueue, setRestockQueue] = useState<VendingRestockQueueItem[]>([]);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [dashboardRes, machinesRes, restockRes] = await Promise.all([
        vendingApiClient('/dashboard'),
        vendingApiClient('/machines?limit=200'),
        vendingApiClient('/restock?limit=20'),
      ]);

      const dashboardJson: VendingDashboardResponse = await dashboardRes.json();
      const machinesJson: VendingMachineListResponse = await machinesRes.json();
      const restockJson: VendingRestockQueueResponse = await restockRes.json();

      setDashboard(dashboardJson.data);
      setMachines(machinesJson.data);
      setRestockQueue(restockJson.data);
      setAsOf(dashboardJson.meta?.as_of ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      setDashboard(null);
      setMachines([]);
      setRestockQueue([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const attentionItems = useMemo(
    () => buildAttentionItems(machines, restockQueue),
    [machines, restockQueue],
  );

  const onlinePct = dashboard && dashboard.total_machines > 0
    ? Math.round((dashboard.online / dashboard.total_machines) * 100)
    : 0;

  return (
    <PageContainer>
      <Header
        title="Vending Operations"
        subtitle={
          asOf
            ? `Monitor machines, inventory, usage and replenishment • Last sync: ${formatUTC(asOf)} UTC`
            : 'Monitor machines, inventory, usage and replenishment'
        }
      >
        <Button variant="outlined" onClick={loadDashboard} disabled={loading}>
          Refresh
        </Button>
      </Header>

      <Box sx={{ px: { xs: 2, md: 3 }, pt: 1 }}>
        <FreshnessBadge asOf={asOf} />
      </Box>

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: COLORS.teal }} />
          </Box>
        )}

        {!loading && error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={loadDashboard}>
                Try again
              </Button>
            }
            sx={{ mb: 3 }}
          >
            {error}
          </Alert>
        )}

        {!loading && dashboard && (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(4, 1fr)',
                },
                gap: 2,
                mb: 3,
              }}
            >
              <KpiCard
                label="Machines"
                value={dashboard.total_machines}
                icon={<VendingMachineIcon />}
              />
              <KpiCard
                label="Online"
                value={dashboard.online}
                sublabel={`${dashboard.online} / ${dashboard.total_machines} • ${onlinePct}%`}
                icon={<Wifi size={22} />}
                accent={COLORS.success}
              />
              <KpiCard
                label="Need Restock"
                value={dashboard.machines_needing_restock}
                icon={<Package size={22} />}
                accent={COLORS.orange}
              />
              <KpiCard
                label="Critical Alerts"
                value={dashboard.open_alerts}
                icon={<AlertTriangle size={22} />}
                accent={dashboard.open_alerts > 0 ? COLORS.red : COLORS.teal}
              />
            </Box>

            <AttentionRequired items={attentionItems} />

            {dashboard.total_machines === 0 && (
              <Alert severity="info">
                No vending machines synced yet. Run a machine sync job in integration-api.
              </Alert>
            )}
          </>
        )}
      </Box>
    </PageContainer>
  );
};

export default VendingDashboardPage;
